import React, { useState } from 'react';
import {
  Dices, Loader2, Wallet,
} from 'lucide-react';
import client from '../../lib/api';
import {
  REWARD_CASH, REWARD_FREESPIN, tierKind,
} from '../../lib/bonusRules';
import { formatDateTime, formatMoney } from '../lib/formatters';

const OUTCOME_FILTERS = [
  { id: '', label: 'Tümü' },
  { id: 'granted', label: 'Verildi' },
  { id: 'skipped', label: 'Atlandı' },
];

const REWARD_OPTIONS = [
  { id: REWARD_FREESPIN, label: 'Freespin', icon: Dices },
  { id: REWARD_CASH, label: 'Bakiye (₺)', icon: Wallet },
];

/** Otomatik tanımlama ayarları: ödül türü, saat penceresi ve günlük hak.
 *
 * Kararın kendisi sunucudadır (services/bonus_auto.py); bu bölüm yalnızca
 * kampanya kaydına yazılacak kuralları toplar. Ödül tek türlüdür: freespin ya
 * da bakiye — seçim kampanyanın iki ödül bayrağını birlikte yazar, sunucu da
 * türü o bayraklardan okur.
 *
 * Kademe tablosu burada **değildir**: elle talep edilen kampanyada da
 * işlediği için ödülün yanında durur (components/BonusTierTable.jsx).
 */
export const BonusAutoAwardFields = ({ form, setForm }) => {
  const kind = tierKind(form);
  const cash = kind === REWARD_CASH;
  const chooseReward = (next) => setForm({
    ...form,
    freespin_enabled: next === REWARD_FREESPIN,
    cash_reward_enabled: next === REWARD_CASH,
  });
  const wager = Number(form.wager_multiplier) || 0;
  return (
    <div className="grid gap-4">
      <div>
        <span className="text-xs text-slate-400 block mb-1.5">Ödül türü</span>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Otomatik ödül türü">
          {REWARD_OPTIONS.map((option) => {
            const Icon = option.icon;
            const on = kind === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={on}
                className={`admin-chip justify-center py-2 ${on ? 'active' : ''}`}
                onClick={() => chooseReward(option.id)}
              >
                <Icon size={12} className="inline mr-1" /> {option.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
          {cash
            ? (wager > 0
              ? `Tutar bonus bakiyesine yazılır ve ×${wager} çevrim şartı uygulanır`
                + ' (Uygunluk Kuralları → Çevrim Şartı).'
              : 'Çevrim şartı 0: tutar doğrudan ana bakiyeye çevrimsiz yazılır.')
            : 'Hak, oyunun sağlayıcısında tanımlanır (Ödül → Freespin ver ayarları).'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs text-slate-400 block mb-1.5">Yatırım saati — başlangıç</span>
          <input
            type="time"
            value={form.deposit_window_start || ''}
            onChange={(e) => setForm({ ...form, deposit_window_start: e.target.value })}
            className="admin-input"
            aria-label="Yatırım saati başlangıcı"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400 block mb-1.5">Yatırım saati — bitiş</span>
          <input
            type="time"
            value={form.deposit_window_end || ''}
            onChange={(e) => setForm({ ...form, deposit_window_end: e.target.value })}
            className="admin-input"
            aria-label="Yatırım saati bitişi"
          />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400 block mb-1.5">Günlük hak</span>
          <input
            type="number"
            min="0"
            max="50"
            value={form.auto_daily_limit}
            onChange={(e) => setForm({ ...form, auto_daily_limit: Number(e.target.value) })}
            className="admin-input"
            aria-label="Günlük hak"
          />
        </label>
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500">
        Saatler Türkiye saatidir ve yatırımın <b>açıldığı</b> ana bakılır: 23:50&apos;de
        açılıp gece yarısından sonra onaylanan yatırım da hak kazanır. İki alanı da boş
        bırakırsanız her saat geçerlidir. Günlük hak: aynı gün içinde üye başına en çok
        kaç yatırım ödüllenir (0 = her uygun yatırıma ayrı hak).
      </p>

      <p className="text-[11px] leading-relaxed text-slate-500">
        Yatırım kademeleri (&quot;1.000 ₺ yatırana 100 spin&quot;) <b>Ödül</b>
        bölümündedir: tablo elle talep edilen kampanyada da işlediği için tek
        yerde durur.
      </p>
    </div>
  );
};

/** Yatırım başına kararlar: "oyuncu neden almadı" sorusunun yanıtı. */
export const BonusAutoAwardLog = ({ bonusId }) => {
  const [data, setData] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (nextOutcome = outcome) => {
    setLoading(true);
    setError('');
    try {
      const response = await client.get(`/admin/bonuses/${bonusId}/auto-awards`, {
        params: { outcome: nextOutcome, limit: 50 },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kararlar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (!data && !loading && !error) {
    return (
      <button type="button" className="admin-btn-secondary w-full" onClick={() => load()}>
        Otomatik tanımlama kayıtlarını göster
      </button>
    );
  }

  return (
    <div className="grid gap-2" data-testid="auto-award-log">
      <div className="flex flex-wrap items-center gap-2">
        {OUTCOME_FILTERS.map((item) => (
          <button
            key={item.id || 'all'}
            type="button"
            className={`admin-chip ${outcome === item.id ? 'active' : ''}`}
            onClick={() => { setOutcome(item.id); load(item.id); }}
          >
            {item.label}
            {item.id && data?.counts ? ` (${data.counts[item.id] || 0})` : ''}
          </button>
        ))}
        {loading && <Loader2 size={13} className="animate-spin text-cyan-400" />}
      </div>
      {error && <p className="text-[12px] text-rose-300">{error}</p>}
      {data && !data.items.length && (
        <p className="text-[12px] text-slate-500">Henüz kayıt yok.</p>
      )}
      {data?.items?.length > 0 && (
        <div className="max-h-64 overflow-y-auto rounded-xl border border-white/5">
          <table className="w-full text-[12px]">
            <thead className="text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left">Üye</th>
                <th className="px-2 py-1.5 text-left">Yatırım</th>
                <th className="px-2 py-1.5 text-left">Yatırım saati</th>
                <th className="px-2 py-1.5 text-left">Sonuç</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-2 py-1.5">{row.username || row.user_id}</td>
                  <td className="px-2 py-1.5">{formatMoney(row.deposit_amount)}</td>
                  <td className="px-2 py-1.5">{formatDateTime(row.deposit_created_at)}</td>
                  <td className="px-2 py-1.5">
                    {row.outcome === 'granted'
                      ? (
                        <span className="text-emerald-300">
                          {row.reward_type === 'cash'
                            ? `${formatMoney(row.bonus_amount)} bonus verildi`
                            : `${row.freespin_count} freespin verildi`}
                        </span>
                      )
                      : <span className="text-amber-300">{row.reason || 'Atlandı'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
